/**
 * sso-api.ts — Settings > Security > Single sign-on.
 *
 * The four contracted operations (CC 0179: getSsoSettings, saveSamlConnection,
 * deleteSamlConnection, listSsoSignInActivity) and the Microsoft half (CC 0184:
 * updateMicrosoftSignIn, addMicrosoftSignInDomain, removeMicrosoftSignInDomain) all
 * go through the typed api-client with the contract's own schemas.
 */
import {
  getSsoSettings as getSsoSettingsOp,
  updateMicrosoftSignIn as updateMicrosoftSignInOp, addMicrosoftSignInDomain as addMicrosoftSignInDomainOp,
  removeMicrosoftSignInDomain as removeMicrosoftSignInDomainOp, saveSamlConnection as saveSamlConnectionOp,
  deleteSamlConnection as deleteSamlConnectionOp, listSsoSignInActivity as listSsoSignInActivityOp,
} from "@v0y4ge/api-client";
import type {
  SsoSettings, SamlConnectionSettings, MicrosoftSignInSettings, MicrosoftSignInDomain,
  SsoSignInActivityItem, SaveSamlConnectionBody, SsoRole,
} from "@v0y4ge/api-contracts";

export type { SsoSettings, SsoRole };
export type SsoSaml = SamlConnectionSettings;
export type SsoMicrosoft = MicrosoftSignInSettings;
export type SsoDomain = MicrosoftSignInDomain;
export type SsoActivityRow = SsoSignInActivityItem;
export type SamlConnectionInput = SaveSamlConnectionBody;

export const getSsoSettings = async (): Promise<SsoSettings> => (await getSsoSettingsOp()).data;
export const saveSamlConnection = async (input: SaveSamlConnectionBody): Promise<SamlConnectionSettings> => (await saveSamlConnectionOp(input)).data;
export const deleteSamlConnection = async (): Promise<boolean> => (await deleteSamlConnectionOp()).data.deleted;
export const listSsoSignInActivity = async (): Promise<SsoSignInActivityItem[]> => (await listSsoSignInActivityOp(20)).data;

/* ── Microsoft half (CC 0184): typed client ── */
/** PATCH /v1/admin/sso/microsoft — { enabled?, defaultRoleKey? } → the Microsoft section. */
export const updateMicrosoftSignIn = async (patch: { enabled?: boolean; defaultRoleKey?: string }) => (await updateMicrosoftSignInOp(patch)).data;
/** POST /v1/admin/sso/microsoft/domains — answers the normalised domain, "pending". */
export const addMicrosoftSignInDomain = async (domain: string) => (await addMicrosoftSignInDomainOp({ domain })).data;
/** DELETE …/domains/{domain} — answers the whole Microsoft section (removing the last verified domain also switches sign-in off). */
export const removeMicrosoftSignInDomain = async (domain: string) => (await removeMicrosoftSignInDomainOp(domain)).data;

/** Words for a refused domain's reason code (MICROSOFT_SIGN_IN_DOMAIN_REASON_CODES). */
export const DOMAIN_REASON_TEXT: Record<string, string> = {
  directory_not_connected: "Your Microsoft integration isn't connected to a directory yet. Connect it on Settings > Integrations > Microsoft 365, then add the domain again.",
  directory_ambiguous: "Your Microsoft registrations are in more than one directory, so we can't tell which one owns this domain. Keep one directory, then add the domain again.",
  directory_permission_missing: "Your Microsoft app registration can't read the directory's domains. Add the Organization.Read.All application permission, grant admin consent again, then add the domain again.",
  domain_not_in_directory: "Your Microsoft directory doesn't list this domain as verified. Only exact domains count: a subdomain must be verified itself.",
  directory_unreachable: "Microsoft couldn't be reached just now. Add the domain again to retry.",
};
/** Words for each reason "Sign in with Microsoft" isn't on the sign-in page (MICROSOFT_SIGN_IN_BLOCKER_CODES). */
export const BLOCKER_TEXT: Record<string, string> = {
  switch_off: "Turn this on to show Sign in with Microsoft on your sign-in page.",
  no_verified_domain: "Add a domain and wait for it to be verified.",
  platform_not_configured: "JRNI still has to connect Microsoft sign-in for your workspace. Nothing more to do here.",
  paused_by_platform: "Microsoft sign-in is paused by JRNI.",
};
export const blockerText = (code: string) => BLOCKER_TEXT[code] ?? `Not shown yet (${code}).`;

export const domainReasonText = (code?: string | null) => (code ? DOMAIN_REASON_TEXT[code] ?? `Refused (${code}).` : "This domain couldn't be verified.");

/** The Entra issuer form the synced-staff-only rule needs (server: invalid_idp_entity_id otherwise). */
export const isMicrosoftIssuer = (v: string) => /^https:\/\/sts\.windows\.net\/[0-9a-f-]{36}\/?$/i.test(v.trim());
export const isDomain = (v: string) => /^(?=.{3,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i.test(v.trim());

/** Words for SSO_REQUEST_INVALID fields[].code (the screen owns the words). */
export const SSO_FIELD_TEXT: Record<string, string> = {
  invalid_value: "This value isn't valid.",
  invalid_idp_entity_id: "Only a Microsoft Entra Identifier (https://sts.windows.net/<tenant id>/) can be used here.",
  invalid_login_url: "Enter the login URL, starting with https://.",
  certificate_required: "Upload or paste the signing certificate.",
  certificate_invalid: "This isn't an RSA X.509 certificate. Upload the .cer or .pem file from your identity provider.",
  certificate_expired: "This certificate has already expired. Download the current one from your identity provider.",
  invalid_default_role: "Choose one of the listed roles.",
  invalid_domain: "Enter a domain such as example.com.",
  too_many_domains: "A workspace can have at most 50 domains. Remove one first.",
  verified_domain_required: "Verify at least one domain before turning Microsoft sign-in on.",
  synced_staff_required: "Microsoft sign-in is always limited to staff synced from your directory.",
};
/** A refused write's message: the first field code in words, else the server's message. */
export const ssoErrorText = (e: unknown): string => {
  const f = (e as { fields?: { code?: string; message?: string }[] })?.fields?.[0];
  return (f?.code && SSO_FIELD_TEXT[f.code]) || f?.message || (e instanceof Error ? e.message : String(e));
};

/** Words for each sign-in refusal reason code (SSO_SIGN_IN_REASON_CODES). */
export const SSO_REASON_TEXT: Record<string, string> = {
  grant_missing: "Not a staff member synced from your directory.",
  grant_inactive: "Their directory staff access is no longer active.",
  email_collision: "Another account already uses this email address.",
  malformed_claims: "Microsoft sent incomplete account details.",
  jit_disabled: "New accounts can't be created by signing in.",
  membership_not_active: "Their membership of this workspace isn't active.",
  directory_grant_changed: "Their directory access changed during sign-in. Try again.",
  authz_denied: "They don't have access to this workspace.",
  config_not_found: "No SAML connection is set up.",
  issuer_not_entra: "The identity provider isn't Microsoft Entra, which this connection requires.",
  name_id_not_object_id: "The identity provider didn't send the Entra object ID as the user identifier.",
  account_link_refused: "The account couldn't be linked to an existing staff member.",
  missing_response: "No response arrived from the identity provider.",
  malformed_response: "The identity provider's response couldn't be read.",
  status_not_success: "The identity provider reported that sign-in failed.",
  issuer_mismatch: "The response came from a different identity provider than the one saved.",
  audience_mismatch: "The response was meant for a different service. Check the Entity ID you gave your identity provider.",
  destination_mismatch: "The response was sent to the wrong address. Check the Reply (ACS) URL.",
  recipient_mismatch: "The response was addressed to a different recipient. Check the Reply (ACS) URL.",
  missing_assertion_id: "The identity provider's response was incomplete.",
  missing_conditions: "The identity provider's response was incomplete.",
  missing_subject_confirmation: "The identity provider's response was incomplete.",
  missing_subject_confirmation_expiry: "The identity provider's response was incomplete.",
  subject_confirmation_expired: "The response arrived too late. Check the server clocks.",
  not_before: "The response isn't valid yet. Check the server clocks.",
  not_on_or_after: "The response has expired. Check the server clocks.",
  session_not_on_or_after: "The identity provider's session had already ended. Check the server clocks.",
  session_not_on_or_after_invalid: "The identity provider sent an end of session that couldn't be read. Check its SAML settings.",
  replay_detected: "This response was already used once and was refused.",
  unsolicited_response: "Sign-in wasn't started from this workspace.",
  in_response_to_unknown: "Sign-in wasn't started from this workspace, or took too long.",
  in_response_to_mismatch: "The response doesn't match the sign-in that was started.",
  in_response_to_incomplete: "The sign-in that was started didn't complete.",
  relay_state_rejected: "The return address after sign-in was refused.",
  email_required: "The identity provider didn't send an email address.",
  validation_error: "The identity provider's response didn't pass checks (for example, its signature didn't match the saved certificate).",
  unknown: "Refused for a reason that wasn't recorded.",
};
export const reasonText = (code?: string | null) => (code ? SSO_REASON_TEXT[code] ?? `Refused (${code}).` : "");
